import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, Clock, Send, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICE_OPTIONS = [
  "Redovan Auto Servis",
  "Hitna Šlep Služba",
  "Kompjuterska Dijagnostika",
  "Kupovina Delova",
];

const inputCls =
  "w-full bg-[#0c0c0f] border border-white/10 focus:border-red-500/70 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", service_type: SERVICE_OPTIONS[0], preferred_date: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Popunite ime i broj telefona");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success("Zahtev uspešno poslat!", {
        description: "Javićemo vam se u najkraćem roku. Za hitne slučajeve pozovite 0677775613.",
      });
      setForm({ name: "", phone: "", service_type: SERVICE_OPTIONS[0], preferred_date: "", message: "" });
    } catch {
      toast.error("Došlo je do greške", { description: "Pokušajte ponovo ili nas pozovite direktno." });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kontakt" className="relative py-24 lg:py-32 px-6 lg:px-12 overflow-hidden">
      <div className="absolute -top-32 right-0 w-[480px] h-[480px] rounded-full bg-red-600/[0.07] blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto grid lg:grid-cols-5 gap-12 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="lg:col-span-2"
        >
          <span className="text-red-500 text-xs font-bold tracking-[0.3em] uppercase">Kontakt & Rezervacije</span>
          <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl text-white mt-3 leading-[0.95]">
            Zakaži Termin <span className="text-red-500">ili Pozovi Šlep</span>
          </h2>
          <p className="text-zinc-400 mt-6 leading-relaxed max-w-md">
            Popunite formu i javićemo vam se za potvrdu termina. Za hitne situacije
            na putu — poziv je uvek najbrži način.
          </p>

          <div className="mt-10 space-y-5">
            <a data-testid="contact-phone-link" href="tel:0677775613" className="flex items-center gap-4 group">
              <span className="w-11 h-11 rounded-xl bg-red-600/12 border border-red-600/25 flex items-center justify-center text-red-500 group-hover:scale-110 transition-transform">
                <Phone size={18} />
              </span>
              <span>
                <span className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500">Telefon (00-24h)</span>
                <span className="text-white font-semibold group-hover:text-red-400 transition-colors">067 777 5613</span>
              </span>
            </a>
            <a data-testid="contact-email-link" href="mailto:autobayserbia@gmail.com" className="flex items-center gap-4 group">
              <span className="w-11 h-11 rounded-xl bg-red-600/12 border border-red-600/25 flex items-center justify-center text-red-500 group-hover:scale-110 transition-transform">
                <Mail size={18} />
              </span>
              <span>
                <span className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500">Email</span>
                <span className="text-white font-semibold group-hover:text-red-400 transition-colors">autobayserbia@gmail.com</span>
              </span>
            </a>
            <a data-testid="contact-address-link" href="https://www.google.com/maps?q=Milo%C5%A1a+Obili%C4%87a+20,+%C4%8Cekmin,+Srbija" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group">
              <span className="w-11 h-11 rounded-xl bg-red-600/12 border border-red-600/25 flex items-center justify-center text-red-500 group-hover:scale-110 transition-transform">
                <MapPin size={18} />
              </span>
              <span>
                <span className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500">Adresa</span>
                <span className="text-white font-semibold group-hover:text-red-400 transition-colors">Miloša Obilića 20, Čekmin</span>
              </span>
            </a>
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-xl bg-red-600/12 border border-red-600/25 flex items-center justify-center text-red-500">
                <Clock size={18} />
              </span>
              <span>
                <span className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500">Radno vreme</span>
                <span className="text-white font-semibold">Servis: Pon-Sub 08-19h | Šlep: 24/7</span>
              </span>
            </div>
          </div>

          <div data-testid="contact-map" className="mt-10 rounded-2xl overflow-hidden border border-white/[0.07] shadow-[0_12px_48px_rgba(0,0,0,0.4)]">
            <iframe
              title="AutoBay lokacija — Miloša Obilića 20, Čekmin"
              src="https://www.google.com/maps?q=Milo%C5%A1a+Obili%C4%87a+20,+%C4%8Cekmin,+Srbija&output=embed"
              className="w-full h-56 border-0"
              loading="lazy"
            />
          </div>
          <p className="text-xs text-zinc-600 mt-3 leading-relaxed">
            Radionica: Miloša Obilića 20, Čekmin · Šlep služba dolazi na vašu lokaciju širom Srbije.
          </p>
        </motion.div>

        <motion.form
          data-testid="contact-booking-form"
          onSubmit={submit}
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="lg:col-span-3 bg-[#121216] border border-white/[0.07] rounded-3xl p-7 sm:p-10"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="booking-name" className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2">Ime i Prezime *</label>
              <input id="booking-name" data-testid="booking-name-input" type="text" value={form.name} onChange={set("name")}
                placeholder="Petar Petrović" className={inputCls} required />
            </div>
            <div>
              <label htmlFor="booking-phone" className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2">Broj Telefona *</label>
              <input id="booking-phone" data-testid="booking-phone-input" type="tel" value={form.phone} onChange={set("phone")}
                placeholder="06x xxx xx xx" className={inputCls} required />
            </div>
            <div>
              <label htmlFor="booking-service" className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2">Tip Usluge</label>
              <select id="booking-service" data-testid="booking-service-select" value={form.service_type} onChange={set("service_type")}
                className={`${inputCls} appearance-none cursor-pointer`}>
                {SERVICE_OPTIONS.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="booking-date" className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2">Željeni Datum / Lokacija</label>
              <input id="booking-date" data-testid="booking-date-input" type="text" value={form.preferred_date} onChange={set("preferred_date")}
                placeholder="npr. Sutra u 10h ili Beograd-Novi Sad" className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="booking-message" className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2">Opis Kvara ili Napomena</label>
              <textarea id="booking-message" data-testid="booking-message-input" rows={4} value={form.message} onChange={set("message")}
                placeholder="Napišite marku vozila i kratak opis problema..." className={`${inputCls} resize-none`} />
            </div>
          </div>
          <button
            data-testid="booking-submit-button"
            type="submit"
            disabled={sending}
            className="mt-7 w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white font-bold py-4 rounded-full transition-all hover:shadow-[0_0_32px_rgba(227,22,30,0.5)] active:scale-[0.98]"
          >
            {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            {sending ? "Slanje..." : "Pošalji Zahtev"}
          </button>
          <p className="text-center text-xs text-zinc-600 mt-4">
            Slanjem forme pristajete da vas kontaktiramo radi potvrde termina.
          </p>
        </motion.form>
      </div>
    </section>
  );
}
